/**
 * Editing an account from the accounts table: each field lives in a different place in
 * zInspector, and only some places are writable with the API key today.
 *
 * | Field                         | Where                         | Writable now            |
 * |-------------------------------|-------------------------------|-------------------------|
 * | account manager, status       | the account's journal task    | yes (tasks)             |
 * | next step                     | a Follow-up task (a to-do)    | yes (tasks)             |
 * | account details (kind, doors…)| zData "Account" area          | no: proposed route 4    |
 * | name, FileMaker ID, market    | the property                  | no: proposed route 5    |
 *
 * The demo's fake zInspector accepts all three, so the table is fully editable there.
 */
import type { ZcrmAccount } from "./accounts";
import {
  ACCOUNT_DETAILS,
  ACCOUNT_STATUS_TASK_FIELD,
  ACTIVITY_DEFAULT_MINUTES,
  ACTIVITY_DONE_STATUS,
  JOURNAL_TASK_PREFIX,
  NEXT_STEP_KIND,
  TASK_TYPE_PK,
  type AccountDetailKey,
} from "./crmConfig";

export type AccountWriteKind = "task" | "zdata" | "property";

export interface AccountWrite {
  kind: AccountWriteKind;
  method: "POST" | "PATCH";
  path: string;
  body: Record<string, unknown>;
  /** For a task create: the fields the create route doesn't take, sent as an edit right after. */
  thenEdit?: Record<string, unknown>;
}

/** What the API key can write today; the fake zInspector also takes the proposed routes. */
export const accountWriteSupport = (
  isFake: boolean,
): Record<AccountWriteKind, boolean> => ({
  task: true,
  zdata: isFake,
  property: isFake,
});

const PROPERTY_FIELDS: Partial<Record<keyof ZcrmAccount, string>> = {
  name: "Name",
  accounting_id: "AccountingID",
  user_group: "UserGroup",
};

const JOURNAL_PRIORITY = 5;
const DETAIL_KEYS = new Set<string>(ACCOUNT_DETAILS.map((d) => d.key));

/** Which zInspector object an account field is written to; null when it can't be edited. */
const TASK_FIELDS = new Set([
  "account_manager_id",
  "status",
  "next_step",
  "next_step_due",
  "next_step_done",
]);

export const accountFieldWriteKind = (
  field: string,
): AccountWriteKind | null => {
  if (TASK_FIELDS.has(field)) return "task";
  if (DETAIL_KEYS.has(field)) return "zdata";
  if (field in PROPERTY_FIELDS) return "property";
  return null;
};

const clean = (value: unknown) => (value == null ? "" : String(value).trim());

const MINUTE_MS = 60_000;
const NEXT_STEP_PRIORITY = 3;
/** A next step dated without a time is due at 9 a.m. local time. */
const NEXT_STEP_HOUR = "T09:00";

const toDueDate = (value: unknown): Date | null => {
  const text = clean(value);
  if (!text) return null;
  const date = new Date(text.length === 10 ? `${text}${NEXT_STEP_HOUR}` : text);
  return Number.isNaN(date.getTime()) ? null : date;
};

const taskDates = (due: Date | null) =>
  due
    ? {
        StartDate: due.toISOString(),
        EndDate: new Date(
          due.getTime() + ACTIVITY_DEFAULT_MINUTES * MINUTE_MS,
        ).toISOString(),
      }
    : {};

type AccountRef = Pick<
  ZcrmAccount,
  | "id"
  | "name"
  | "journal_task_id"
  | "account_manager_id"
  | "next_step_task_id"
  | "next_step_source"
>;

/** The journal task edit (manager, status), creating the task when the account has none. */
const journalWrite = (
  account: AccountRef,
  fields: Record<string, unknown>,
): AccountWrite => {
  if (account.journal_task_id) {
    return {
      kind: "task",
      method: "PATCH",
      path: `/task/edit/${account.journal_task_id}/`,
      body: fields,
    };
  }
  const { ManagedBy, AssignedToUser, ...rest } = fields;
  const manager =
    ManagedBy ??
    (account.account_manager_id
      ? Number(account.account_manager_id)
      : undefined);
  return {
    kind: "task",
    method: "POST",
    path: "/task/create/",
    body: {
      Action: TASK_TYPE_PK["Follow-up"],
      Priority: JOURNAL_PRIORITY,
      Property: Number(account.id),
      Description: `${JOURNAL_TASK_PREFIX} — ${account.name}`,
      ManagedBy: manager,
      AssignedToUser: AssignedToUser ?? manager,
    },
    ...(Object.keys(rest).length ? { thenEdit: rest } : {}),
  };
};

/** Setting, changing, clearing or completing the next step. */
const nextStepWrites = (
  account: AccountRef,
  data: Partial<Record<string, unknown>>,
  changed: string[],
): AccountWrite[] => {
  const editable =
    account.next_step_source === "follow-up" && account.next_step_task_id;
  if (data.next_step_done) {
    return editable
      ? [
          {
            kind: "task",
            method: "PATCH",
            path: `/task/edit/${account.next_step_task_id}/`,
            body: { Status: ACTIVITY_DONE_STATUS, IncludeCalendar: false },
          },
        ]
      : [];
  }
  if (!changed.includes("next_step") && !changed.includes("next_step_due")) {
    return [];
  }
  const text = clean(data.next_step);
  const due = toDueDate(data.next_step_due);
  if (editable) {
    return [
      {
        kind: "task",
        method: "PATCH",
        path: `/task/edit/${account.next_step_task_id}/`,
        // Clearing the text closes the step.
        body: text
          ? { Description: text, ...taskDates(due) }
          : { Status: ACTIVITY_DONE_STATUS, IncludeCalendar: false },
      },
    ];
  }
  if (!text) return [];
  const manager = account.account_manager_id
    ? Number(account.account_manager_id)
    : undefined;
  return [
    {
      kind: "task",
      method: "POST",
      path: "/task/create/",
      body: {
        Action: TASK_TYPE_PK["Follow-up"],
        Priority: NEXT_STEP_PRIORITY,
        Property: Number(account.id),
        Description: text,
        // Without a date it's due today, so it shows as the next step.
        ...taskDates(due ?? new Date()),
        IncludeCalendar: true,
        ManagedBy: manager,
        AssignedToUser: manager,
      },
      thenEdit: { EntranceInstruction: NEXT_STEP_KIND },
    },
  ];
};

/**
 * The requests that save the changed fields. The account manager and a status set by hand live
 * on the journal task (created if the account has none); the next step is a Follow-up task.
 */
export const buildAccountWrites = (
  account: AccountRef,
  data: Partial<Record<string, unknown>>,
  previous: Partial<Record<string, unknown>>,
): AccountWrite[] => {
  const changed = Object.keys(data).filter(
    (key) => clean(data[key]) !== clean(previous[key]),
  );
  const writes: AccountWrite[] = [];

  const journal: Record<string, unknown> = {};
  if (changed.includes("account_manager_id")) {
    const manager = data.account_manager_id
      ? Number(data.account_manager_id)
      : null;
    journal.ManagedBy = manager;
    journal.AssignedToUser = manager;
  }
  if (changed.includes("status")) {
    // An empty status goes back to the one the engagement gives.
    journal[ACCOUNT_STATUS_TASK_FIELD] = clean(data.status);
  }
  if (Object.keys(journal).length) writes.push(journalWrite(account, journal));

  writes.push(...nextStepWrites(account, data, changed));

  const details = ACCOUNT_DETAILS.filter(({ key }) =>
    changed.includes(key),
  ).map(({ key, detail }) => ({
    detail,
    value: clean(data[key as AccountDetailKey]),
  }));
  if (details.length) {
    writes.push({
      kind: "zdata",
      method: "POST",
      path: "/zdata/save/",
      body: { Property: account.id, area_name: "Account", values: details },
    });
  }

  const propertyBody = Object.fromEntries(
    Object.entries(PROPERTY_FIELDS)
      .filter(([key]) => changed.includes(key))
      .map(([key, field]) => [field, clean(data[key])]),
  );
  if (Object.keys(propertyBody).length) {
    writes.push({
      kind: "property",
      method: "PATCH",
      path: `/property/edit/${account.id}/`,
      body: propertyBody,
    });
  }
  return writes;
};

/** Thrown when an edit needs a route the API key can't use yet. */
export class AccountWriteNotSupported extends Error {
  constructor(readonly writes: AccountWrite[]) {
    super(
      "zInspector's public API can't save this field yet: edit it in the zInspector dashboard.",
    );
  }
}

export const accountWriteError = (
  writes: AccountWrite[],
  support: Record<AccountWriteKind, boolean>,
): AccountWriteNotSupported | null => {
  const blocked = writes.filter((write) => !support[write.kind]);
  return blocked.length ? new AccountWriteNotSupported(blocked) : null;
};
